import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Check, X } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '@avagency/frontend/components/ui/card';
import { Button } from '@avagency/frontend/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@avagency/frontend/components/ui/table';
import { Alert, AlertDescription } from '@avagency/frontend/components/ui/alert';
import { ErrorState } from '@avagency/frontend/components/shell/ErrorState';
import { LoadingState } from '@avagency/frontend/components/shell/LoadingState';
import { cn } from '@avagency/frontend/lib/utils';
import {
  fetchPurchaseOrder,
  sendPO,
  cancelPO,
  receivePO,
  type PODetail as PODetailType,
  type POStatus,
} from '../api';
import { StatusBadge } from '../components/StatusBadge';
import { fmtDate, fmtDateTime, currencyFmt } from '../lib/format';

export function PODetail() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['popilot', 'purchase-order', id],
    queryFn: () => fetchPurchaseOrder(id!),
    enabled: !!id,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['popilot', 'purchase-order', id] });
    qc.invalidateQueries({ queryKey: ['popilot', 'purchase-orders'] });
  };

  const sendMut = useMutation({ mutationFn: () => sendPO(id!), onSuccess: invalidate });
  const cancelMut = useMutation({ mutationFn: () => cancelPO(id!), onSuccess: invalidate });
  const receiveMut = useMutation({ mutationFn: () => receivePO(id!), onSuccess: invalidate });

  if (isLoading) return <LoadingState rows={6} />;
  if (error) return <ErrorState error={error} retry={() => refetch()} />;
  if (!data) return null;

  const po = data.purchase_order;
  const anyPending = sendMut.isPending || cancelMut.isPending || receiveMut.isPending;
  const mutErr = sendMut.error ?? cancelMut.error ?? receiveMut.error;

  return (
    <div className="space-y-6">
      <Link
        to="/purchase-orders"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Purchase Orders
      </Link>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">{po.po_number}</h1>
          <StatusBadge status={po.status} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <TransitionButtons
            status={po.status}
            anyPending={anyPending}
            onSend={() => sendMut.mutate()}
            onCancel={() => cancelMut.mutate()}
            onReceive={() => receiveMut.mutate()}
          />
        </div>
      </div>

      {mutErr && (
        <Alert variant="destructive">
          <AlertDescription>{(mutErr as Error).message}</AlertDescription>
        </Alert>
      )}

      <POTimeline po={po} />

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <MetaItem label="Supplier" value={po.supplier_name ?? po.supplier_id} />
        <MetaItem label="Total" value={currencyFmt(po.total_cost, po.currency)} />
        <MetaItem label="Line Items" value={String(po.line_item_count)} />
        <MetaItem label="Expected Arrival" value={fmtDate(po.expected_arrival)} />
        <MetaItem label="Created" value={fmtDate(po.created_at)} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Line Items</CardTitle>
        </CardHeader>
        <CardContent>
          {po.line_items.length === 0 ? (
            <p className="text-sm text-muted-foreground">No line items.</p>
          ) : (
            <div className="rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>SKU</TableHead>
                    <TableHead>Supplier SKU</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Unit Cost</TableHead>
                    <TableHead className="text-right">Line Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {po.line_items.map((li) => (
                    <TableRow key={`${li.sku}:${li.supplier_sku ?? ''}`}>
                      <TableCell className="font-mono text-xs">{li.sku}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {li.supplier_sku ?? <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>
                        {li.title ?? <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{li.qty}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {currencyFmt(li.unit_cost, po.currency)}
                      </TableCell>
                      <TableCell className="text-right font-semibold tabular-nums">
                        {currencyFmt(li.line_total, po.currency)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={5} className="text-right font-semibold">
                      Total
                    </TableCell>
                    <TableCell className="text-right font-bold tabular-nums">
                      {currencyFmt(po.total_cost, po.currency)}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Status timeline ─────────────────────────────────────────────

const STEPS: { key: POStatus; label: string; dateField: keyof PODetailType }[] = [
  { key: 'draft', label: 'Draft', dateField: 'created_at' },
  { key: 'sent', label: 'Sent', dateField: 'sent_at' },
  { key: 'confirmed', label: 'Confirmed', dateField: 'confirmed_at' },
  { key: 'received', label: 'Received', dateField: 'received_at' },
];

const STATUS_ORDER: Record<POStatus, number> = {
  draft: 0,
  sent: 1,
  confirmed: 2,
  received: 3,
  cancelled: 99,
};

function POTimeline({ po }: { po: PODetailType }) {
  if (po.status === 'cancelled') {
    return (
      <Card>
        <CardContent className="flex items-center gap-3 py-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive text-destructive-foreground">
            <X className="h-5 w-5" />
          </div>
          <div>
            <div className="font-semibold">Cancelled</div>
            {po.cancelled_at && (
              <div className="text-xs text-muted-foreground">
                {fmtDateTime(po.cancelled_at)}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  const currentOrder = STATUS_ORDER[po.status];
  // A "received" PO is the terminal success state — all steps including
  // "received" itself should render as done (filled), not active.
  const isTerminalSuccess = po.status === 'received';

  return (
    <Card className="po-card-glow">
      <CardContent className="py-6">
        <ol className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          {STEPS.map(({ key, label, dateField }, idx) => {
            const stepOrder = STATUS_ORDER[key];
            const isDone = isTerminalSuccess
              ? stepOrder <= currentOrder
              : stepOrder < currentOrder;
            const isActive = !isTerminalSuccess && key === po.status;
            const dateVal = po[dateField] as string | undefined;
            const isLastStep = idx === STEPS.length - 1;

            return (
              <li key={key} className="relative flex flex-1 items-center gap-3 sm:flex-col sm:items-center sm:gap-2">
                {/* Connector line between steps (desktop only) */}
                {!isLastStep && (
                  <div
                    className={cn(
                      'po-timeline-connector hidden sm:block',
                      isDone && 'done',
                    )}
                    aria-hidden
                  />
                )}
                <div
                  className={cn(
                    'relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold transition-all duration-300',
                    isDone && 'bg-primary text-primary-foreground shadow-[0_0_0_3px_hsl(153_39%_49%_/_0.15)]',
                    isActive && 'bg-primary/15 text-primary ring-2 ring-primary ring-offset-2 ring-offset-card',
                    !isDone && !isActive && 'bg-muted text-muted-foreground',
                  )}
                >
                  {isDone ? <Check className="h-4 w-4" strokeWidth={2.5} /> : idx + 1}
                </div>
                <div className="sm:text-center">
                  <div className={cn(
                    'text-sm font-medium',
                    isActive && 'text-primary',
                    !isDone && !isActive && 'text-muted-foreground',
                  )}>
                    {label}
                  </div>
                  {dateVal ? (
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {fmtDate(dateVal)}
                    </div>
                  ) : isActive ? (
                    <div className="text-xs text-primary/60 mt-0.5">In progress</div>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
}

// ─── Transition action buttons ────────────────────────────────────

function TransitionButtons({
  status,
  anyPending,
  onSend,
  onCancel,
  onReceive,
}: {
  status: POStatus;
  anyPending: boolean;
  onSend: () => void;
  onCancel: () => void;
  onReceive: () => void;
}) {
  const canSend = status === 'draft';
  const canReceive = status === 'confirmed';
  const canCancel = status === 'draft' || status === 'sent' || status === 'confirmed';

  if (!canSend && !canReceive && !canCancel) return null;

  return (
    <>
      {canSend && (
        <Button onClick={onSend} disabled={anyPending} aria-busy={anyPending}>
          Send to Supplier
        </Button>
      )}
      {canReceive && (
        <Button onClick={onReceive} disabled={anyPending} aria-busy={anyPending}>
          Mark Received
        </Button>
      )}
      {canCancel && (
        <Button
          variant="destructive"
          onClick={onCancel}
          disabled={anyPending}
          aria-busy={anyPending}
        >
          Cancel PO
        </Button>
      )}
    </>
  );
}

// ─── Meta item ───────────────────────────────────────────────────

function MetaItem({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="space-y-1 py-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
        <div className="text-sm font-medium">{value}</div>
      </CardContent>
    </Card>
  );
}
